import React from 'react';
import {
  Box,
  FormControl,
  Select,
  MenuItem,
  Chip,
  useTheme,
} from '@mui/material';
import { font } from '../../utils/fontHelper';

export type WatchedDatePrecision = 'day' | 'month' | 'year';

interface WatchedDateSelectorProps {
  value: Date;
  precision: WatchedDatePrecision;
  onChange: (date: Date, precision: WatchedDatePrecision) => void;
  movieReleaseYear?: number;
}

const MONTHS = [
  { value: 1, label: 'January' },
  { value: 2, label: 'February' },
  { value: 3, label: 'March' },
  { value: 4, label: 'April' },
  { value: 5, label: 'May' },
  { value: 6, label: 'June' },
  { value: 7, label: 'July' },
  { value: 8, label: 'August' },
  { value: 9, label: 'September' },
  { value: 10, label: 'October' },
  { value: 11, label: 'November' },
  { value: 12, label: 'December' },
];

function getDaysInMonth(year: number, month: number): number {
  return new Date(year, month, 0).getDate(); // month is 1-indexed
}

function getMonday(date: Date): Date {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  const day = d.getDay();
  const diff = day === 0 ? -6 : 1 - day;
  d.setDate(d.getDate() + diff);
  return d;
}

function isSameDay(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

const WatchedDateSelector: React.FC<WatchedDateSelectorProps> = ({
  value,
  precision,
  onChange,
  movieReleaseYear,
}) => {
  const theme = useTheme();
  const now = new Date();
  const currentYear = now.getFullYear();
  const monday = getMonday(now);

  const effectiveReleaseYear = movieReleaseYear ? Math.min(movieReleaseYear, currentYear) : null;
  const fiveYearsAgoYear = currentYear - 5;
  // Only show "5+ years ago" when the movie is actually that old
  const showFiveYearsAgo = !effectiveReleaseYear || effectiveReleaseYear <= fiveYearsAgoYear;

  // Year dropdown options
  const earliestYear = movieReleaseYear ?? 1900;
  const yearOptions: number[] = [];
  for (let y = currentYear; y >= earliestYear; y--) {
    yearOptions.push(y);
  }

  // Derived display values from value + precision
  const displayYear = value.getFullYear();
  const displayMonth = precision !== 'year' ? value.getMonth() + 1 : null;
  const displayDay = precision === 'day' ? value.getDate() : null;

  // Day options for selected year+month
  const dayOptions = displayMonth
    ? Array.from({ length: getDaysInMonth(displayYear, displayMonth) }, (_, i) => i + 1)
    : [];

  // Active chip detection
  const todayNorm = new Date(now); todayNorm.setHours(0, 0, 0, 0);
  const isToday = precision === 'day' && isSameDay(value, todayNorm);
  const isThisWeek = precision === 'day' && isSameDay(value, monday) && !isToday;
  const isThisMonth = precision === 'month' && displayYear === currentYear && displayMonth === now.getMonth() + 1;
  const isThisYear = precision === 'year' && displayYear === currentYear;
  const isReleaseYear = !!effectiveReleaseYear && precision === 'year' && displayYear === effectiveReleaseYear && !isThisYear;
  const isFiveYearsAgo = precision === 'year' && displayYear === fiveYearsAgoYear;

  // Chip handlers
  const handleTodayClick = () => {
    const d = new Date(); d.setHours(0, 0, 0, 0);
    onChange(d, 'day');
  };

  const handleThisWeekClick = () => {
    onChange(getMonday(now), 'day');
  };

  const handleThisMonthClick = () => {
    onChange(new Date(currentYear, now.getMonth(), 1), 'month');
  };

  const handleThisYearClick = () => {
    onChange(new Date(currentYear, 0, 1), 'year');
  };

  const handleReleaseYearClick = () => {
    if (!effectiveReleaseYear) return;
    onChange(new Date(effectiveReleaseYear, 0, 1), 'year');
  };

  const handleFiveYearsAgoClick = () => {
    onChange(new Date(fiveYearsAgoYear, 0, 1), 'year');
  };

  // Dropdown handlers
  const handleYearChange = (newYear: number) => {
    const newDate = new Date(value);
    newDate.setFullYear(newYear);
    if (precision !== 'year') {
      const daysInMonth = getDaysInMonth(newYear, value.getMonth() + 1);
      if (precision === 'day' && newDate.getDate() > daysInMonth) {
        newDate.setDate(daysInMonth);
      } else if (precision === 'month') {
        newDate.setDate(1);
      }
    }
    onChange(newDate, precision);
  };

  const handleMonthChange = (newMonth: number | null) => {
    if (newMonth === null) {
      onChange(new Date(displayYear, 0, 1), 'year');
    } else {
      const daysInNewMonth = getDaysInMonth(displayYear, newMonth);
      const currentDay = precision === 'day' ? Math.min(value.getDate(), daysInNewMonth) : 1;
      const newDate = new Date(displayYear, newMonth - 1, currentDay);
      onChange(newDate, precision === 'year' ? 'month' : precision);
    }
  };

  const handleDayChange = (newDay: number | null) => {
    if (newDay === null) {
      onChange(new Date(displayYear, value.getMonth(), 1), 'month');
    } else {
      onChange(new Date(displayYear, value.getMonth(), newDay), 'day');
    }
  };

  const getChipStyle = (isActive: boolean) => ({
    fontFamily: font(),
    fontSize: 13,
    fontWeight: 500,
    height: 32,
    backgroundColor: isActive
      ? theme.palette.primary.main
      : theme.palette.mode === 'dark'
        ? 'rgba(255,255,255,0.08)'
        : 'rgba(0,0,0,0.06)',
    color: isActive
      ? theme.palette.primary.contrastText
      : theme.palette.text.primary,
    '&:hover': {
      backgroundColor: isActive
        ? theme.palette.primary.dark
        : theme.palette.mode === 'dark'
          ? 'rgba(255,255,255,0.12)'
          : 'rgba(0,0,0,0.1)',
    },
  });

  const selectStyles = {
    borderRadius: 2.5,
    backgroundColor: theme.palette.mode === 'dark' ? 'rgba(0,0,0,0.4)' : 'rgba(255,255,255,0.4)',
    minHeight: 44,
    fontFamily: font(),
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)',
    },
    '&:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)',
    },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: theme.palette.mode === 'dark' ? 'rgba(125, 122, 255, 0.9)' : 'rgba(54, 52, 163, 0.9)',
    },
    '& .MuiSelect-select': {
      fontFamily: font(),
      fontSize: 15,
      color: theme.palette.text.primary,
      py: 1.25,
      display: 'flex',
      alignItems: 'center',
    },
  };

  const menuProps = {
    style: { zIndex: 1500 },
    PaperProps: {
      sx: {
        maxHeight: 300,
        backgroundColor: theme.palette.mode === 'dark'
          ? 'rgba(30, 30, 30, 0.98)'
          : 'rgba(255, 255, 255, 0.98)',
        backdropFilter: 'blur(20px)',
      },
    },
  };

  const menuItemSx = { fontFamily: font(), fontSize: 15 };

  return (
    <Box>
      {/* Quick-select chips */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mb: 1.5 }}>
        <Chip
          label="Today"
          onClick={handleTodayClick}
          clickable
          size="small"
          sx={getChipStyle(isToday)}
        />
        <Chip
          label="This week"
          onClick={handleThisWeekClick}
          clickable
          size="small"
          sx={getChipStyle(isThisWeek)}
        />
        <Chip
          label="This month"
          onClick={handleThisMonthClick}
          clickable
          size="small"
          sx={getChipStyle(isThisMonth)}
        />
        <Chip
          label="This year"
          onClick={handleThisYearClick}
          clickable
          size="small"
          sx={getChipStyle(isThisYear)}
        />
        {effectiveReleaseYear && (
          <Chip
            label={`Release Year: ${effectiveReleaseYear}`}
            onClick={handleReleaseYearClick}
            clickable
            size="small"
            sx={getChipStyle(isReleaseYear)}
          />
        )}
        {showFiveYearsAgo && (
          <Chip
            label="5+ years ago"
            onClick={handleFiveYearsAgoClick}
            clickable
            size="small"
            sx={getChipStyle(isFiveYearsAgo)}
          />
        )}
      </Box>

      {/* Year / Month / Day dropdowns */}
      <Box sx={{ display: 'flex', flexDirection: 'row', gap: 1 }}>
        {/* Year */}
        <FormControl sx={{ flex: 1.2 }}>
          <Select
            value={displayYear}
            onChange={(e) => handleYearChange(Number(e.target.value))}
            sx={selectStyles}
            MenuProps={menuProps}
          >
            {yearOptions.map((y) => (
              <MenuItem key={y} value={y} sx={menuItemSx}>{y}</MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Month */}
        <FormControl sx={{ flex: 1.5 }}>
          <Select
            value={displayMonth ?? ''}
            onChange={(e) => {
              const val = e.target.value;
              handleMonthChange(val === '' ? null : Number(val));
            }}
            displayEmpty
            renderValue={(selected) => {
              if (!selected) {
                return (
                  <span style={{ color: theme.palette.text.secondary, fontStyle: 'italic' }}>
                    Month
                  </span>
                );
              }
              return MONTHS.find((m) => m.value === selected)?.label || '';
            }}
            sx={selectStyles}
            MenuProps={menuProps}
          >
            {displayMonth !== null && (
              <MenuItem value="" sx={{ ...menuItemSx, fontStyle: 'italic', color: theme.palette.text.secondary }}>
                Clear
              </MenuItem>
            )}
            {MONTHS.map(({ value: mv, label }) => (
              <MenuItem key={mv} value={mv} sx={menuItemSx}>{label}</MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Day */}
        <FormControl sx={{ flex: 0.8 }}>
          <Select
            value={displayDay ?? ''}
            onChange={(e) => {
              const val = e.target.value;
              handleDayChange(val === '' ? null : Number(val));
            }}
            displayEmpty
            disabled={displayMonth === null}
            renderValue={(selected) => {
              if (!selected) {
                return (
                  <span style={{ color: theme.palette.text.secondary, fontStyle: 'italic' }}>
                    Day
                  </span>
                );
              }
              return String(selected);
            }}
            sx={{
              ...selectStyles,
              opacity: displayMonth === null ? 0.45 : 1,
            }}
            MenuProps={menuProps}
          >
            {displayDay !== null && (
              <MenuItem value="" sx={{ ...menuItemSx, fontStyle: 'italic', color: theme.palette.text.secondary }}>
                Clear
              </MenuItem>
            )}
            {dayOptions.map((d) => (
              <MenuItem key={d} value={d} sx={menuItemSx}>{d}</MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>
    </Box>
  );
};

export default WatchedDateSelector;
